import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import * as Realm from 'realm-web';
import { saveAs } from 'file-saver';
import { DocumentIcon, PlusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import colors from '../config/colors';
import jahresrahmendienstplanTemplate from '../templates/Jahresrahmendienstplan.xlsx';
import urlaubsplanTemplate from '../templates/Urlaubsplan.xlsx';
import wochenarbeitsplanTemplate from '../templates/Wochenarbeitsplan.xlsx';

const Uploads = ({ setLoggedIn }) => {
  const [employee, setEmployee] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [holidayData, setHolidayData] = useState(null);
  const [weeklyPlanData, setWeeklyPlanData] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const token = localStorage.getItem('accessToken');
  const navigate = useNavigate();
  const app = new Realm.App({ id: process.env.REACT_APP_MONGO_APP });
  const stayLoggedIn = localStorage.getItem('stayLoggedIn') === 'true';

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    validateToken(token)
      .then(async isValid => {
        if (!isValid) {
          if (stayLoggedIn) {
            const refreshed = await refreshAccessToken();
            if (!refreshed) {
              setLoggedIn(false);
              navigate('/login');
            } else {
              const newToken = app.currentUser.accessToken;
              const isValidNewToken = await validateToken(newToken);
              if (!isValidNewToken) {
                setLoggedIn(false);
                navigate('/login');
              } else {
                setLoggedIn(true);
              }
            }
          } else {
            setLoggedIn(false);
            navigate('/login');
          }
        } else {
          setLoggedIn(true);
        }
      })
      .catch(error => {
        setLoggedIn(false);
        navigate('/login');
      });
  }, [token, setLoggedIn, navigate, stayLoggedIn]);

  const validateToken = (token) => {
    return new Promise((resolve, reject) => {
      try {
        let requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Request-Headers': '*',
          },
          body: JSON.stringify({ token: token }),
          redirect: 'follow'
        };

        fetch(process.env.REACT_APP_EMPLOYEE_URL, requestOptions)
          .then(response => response.json())
          .then(result => {
            if (result.error) {
              resolve(false);
            } else {
              setEmployee(result);
              resolve(true);
            }
          })
          .catch(error => {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  };

  const refreshAccessToken = async () => {
    try {
      await app.currentUser.refreshAccessToken();
      const newAccessToken = app.currentUser.accessToken;
      localStorage.setItem('accessToken', newAccessToken);
      return true;
    } catch (error) {
      return false;
    }
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = function (e) {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = "Jahrespläne";
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      setFileData(jsonData);
    };
    reader.readAsArrayBuffer(file);
  };

  const onHolidayDrop = (acceptedFiles) => {
    console.log("Holiday file dropped:", acceptedFiles);
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      console.log("Holiday file loaded successfully");
      let workbook = null;
      try {
        workbook = XLSX.read(event.target.result, {
          type: 'array',
        });
        console.log("Workbook loaded. Available sheets:", workbook.SheetNames);
      } catch (error) {
        console.error("Unsupported file format", error);
        setUploadStatus('error');
        return;
      }
  
      const sheet_name = "Urlaubspläne";
      console.log("Using sheet:", sheet_name);
      const sheet = workbook.Sheets[sheet_name];
      if (!sheet) {
        console.error("Sheet not found");
        setUploadStatus('error');
        return;
      }
      
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      const dateRow = jsonData[3]; 

      // Convert any Excel date serial numbers to JavaScript Date objects
      for (let i = 0; i < dateRow.length; i++) {
        const cell = dateRow[i];
        if (typeof cell === 'number') {
          // Excel date serial numbers can be converted to JS dates
          // Excel's epoch starts on 1899-12-30
          const excelEpoch = new Date(1899, 11, 30);
          const millisecondsPerDay = 24 * 60 * 60 * 1000;
          dateRow[i] = new Date(excelEpoch.getTime() + cell * millisecondsPerDay);
          console.log(`Converted Excel date at index ${i}: ${cell} -> ${dateRow[i]}`);
        } else if (cell instanceof Date) {
          console.log(`Date already in correct format at index ${i}: ${cell}`);
        }
      }

      const absenceTypeMapping = {
        '1': 'Urlaub',
        'U': 'Urlaub',
        'UoB': 'Urlaub ohne Beleg',
        'GZoB': 'Gleittag ohne Beleg',
        'AZKoB': 'AZK ohne Beleg',
        'AUoB': 'Krank ohne Beleg',
        'SU': 'Sonderurlaub',
        'BU': 'Bildungsurlaub',
        'GZ': 'Gleittag',
        'AZK': 'AZK',
        'AU': 'Krank',
        'KFF': 'Krank Ferienfrei',
        'Kk': 'Kind krank',
        'KG': 'Krank mit Krankengeld',
        'KvertAZ': 'Krank vertragliche Arbeitszeit'
      };

      const validAbsenceTypes = Object.keys(absenceTypeMapping);
      const holidays = {};

      // Alle Mitarbeiterzeilen durchlaufen, beginnend mit Zeile 4
      for (let rowIndex = 4; rowIndex < jsonData.length; rowIndex++) {
        const holidayRow = jsonData[rowIndex];
        
        // Überprüfen, ob die Zeile gültige Mitarbeiterdaten enthält
        if (!holidayRow || !holidayRow[0] || !holidayRow[2] || typeof holidayRow[2] !== 'number') {
          console.log(`Skipping row ${rowIndex}: Invalid employee data format`, holidayRow);
          continue;
        }

        const employeeId = holidayRow[2];
        const employeeName = holidayRow[0];
        
        if (!holidays[employeeId]) {
          holidays[employeeId] = [];
        }
        
        console.log(`Processing holiday plan for employee: ${employeeName}, ID: ${employeeId}`);

        for (let j = 8; j < holidayRow.length; j++) {
          const cellValue = holidayRow[j];
          if (cellValue === 1 || (typeof cellValue === 'string' && validAbsenceTypes.includes(cellValue))) {
            const dateValue = dateRow[j];
            
            if (dateValue && dateValue instanceof Date) {
              const day = dateValue.getDate();
              const month = dateValue.getMonth() + 1;
              
              console.log(`Found holiday entry for ${employeeName} (${day}.${month}): ${cellValue}`);
              
              holidays[employeeId].push({
                date: `${day}.${month}`,
                status: absenceTypeMapping[cellValue === 1 ? '1' : cellValue],
              });
            }
          }
        }
        
        console.log(`Found ${holidays[employeeId].length} holiday entries for employee ${employeeId} (${employeeName})`);
      }
      
      console.log("Final holiday data:", holidays);
      
      if (Object.keys(holidays).length === 0) {
        console.error('No valid holiday data found');
        setUploadStatus('error');
        return;
      }
      
      const token = localStorage.getItem('accessToken');
      const requestBody = JSON.stringify({
        token,
        holidayPlans: holidays,
      });
      
      const requestOptions = {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody,
      };

      fetch("https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/addHolidayVersion2", requestOptions)
        .then(response => response.json())
        .then(result => {
          if (result.success) {
            console.log("Holiday data uploaded successfully to version 2:", result);
            setUploadStatus('success');
          } else {
            console.error("Error uploading holiday data to version 2:", result);
            setUploadStatus('error');
          }
        })
        .catch(error => {
          console.error("Error uploading holiday data to version 2:", error);
          setUploadStatus('error');
        });

      fetch("https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/addHolidayPlan", requestOptions)
        .then(response => response.json())
        .then(result => {
          if (result.success) {
            console.log("Holiday data uploaded successfully to original endpoint:", result);
          } else {
            console.error("Error uploading holiday data to original endpoint:", result);
          }
        })
        .catch(error => {
          console.error("Error uploading holiday data to original endpoint:", error);
        });
    };
  
    reader.readAsArrayBuffer(file);
  };

  const onWeeklyPlanDrop = (acceptedFiles) => {
    console.log("Weekly plan file dropped:", acceptedFiles);
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      console.log("Weekly plan file loaded successfully");
      let workbook = null;
      try {
        workbook = XLSX.read(event.target.result, {
          type: 'array',
          cellFormula: false, // Ignoriere Formeln, lese nur die tatsächlichen Werte
          cellText: true // Lese Zellen als Text
        });
        console.log("Workbook loaded. Available sheets:", workbook.SheetNames);
      } catch (error) {
        console.error("Unsupported file format", error);
        setUploadStatus('error');
        return;
      }
      
      // Der Sheet-Name könnte variieren, nehmen wir das erste Sheet
      const sheet_name = workbook.SheetNames[0];
      console.log("Using sheet:", sheet_name);
      const sheet = workbook.Sheets[sheet_name];
      if (!sheet) {
        console.error("Sheet not found");
        setUploadStatus('error');
        return;
      }
      
      // Zeitkonverter-Funktion für Excel-Zeitwerte
      const excelTimeToString = (excelTime) => {
        if (excelTime === undefined || excelTime === null || excelTime === '') return '';
        
        // Für Textwerte direkt zurückgeben (wie '14:30')
        if (typeof excelTime === 'string') {
          // Überprüfe, ob es ein Zeitformat ist (enthält :)
          if (excelTime.includes(':')) {
            return excelTime;
          }
          // Versuche zu parsen, falls es ein numerischer String ist
          if (!isNaN(Number(excelTime))) {
            excelTime = Number(excelTime);
          } else {
            return excelTime;
          }
        }

        // Für numerische Werte (Excel speichert Zeiten als Dezimalbrüche eines Tages)
        if (typeof excelTime === 'number') {
          // Multipliziere mit 24 für Stunden
          const totalHours = excelTime * 24;
          const hours = Math.floor(totalHours);
          // Multipliziere den Rest mit 60 für Minuten
          const minutes = Math.round((totalHours - hours) * 60);
          // Formatiere als "HH:MM"
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        
        return '';
      };
      
      // Verwende die Option {header: 1}, um Zeilenarray zu bekommen
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
      
      // Kalenderwoche und Zeitraum-Informationen extrahieren
      let kw = "";
      let zeitraum = "";
      
      // KW und Zeitraum-Informationen suchen (normalerweise in den ersten Zeilen)
      for (let i = 0; i < 10; i++) {
        const row = jsonData[i] || [];
        for (let j = 0; j < row.length; j++) {
          const cell = row[j];
          if (cell && typeof cell === 'string') {
            if (cell.includes('KW:')) {
              if (row[j+1]) kw = row[j+1];
            }
            if (cell.includes('Woche:')) {
              if (row[j+1]) zeitraum = row[j+1];
            }
          }
        }
      }

      console.log("Kalenderwoche:", kw);
      console.log("Zeitraum:", zeitraum);
      
      // Daten für jeden Wochentag berechnen
      let wochentage = {
        Montag: '',
        Dienstag: '',
        Mittwoch: '',
        Donnerstag: '',
        Freitag: '',
        Samstag: '',
        Sonntag: ''
      };
      
      // Versuche, das Startdatum aus dem Zeitraum zu extrahieren
      if (zeitraum) {
        const match = zeitraum.match(/(\d{2})\.(\d{2})\.(\d{4})/);
        if (match) {
          const [_, day, month, year] = match;
          try {
            // Erstelle ein Datum-Objekt für den ersten Tag der Woche (Montag)
            const startDate = new Date(`${year}-${month}-${day}`);
            
            // Prüfe, ob das Datum gültig ist
            if (!isNaN(startDate.getTime())) {
              // Berechne die Daten für jeden Wochentag
              const tageDE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
              
              for (let i = 0; i < tageDE.length; i++) {
                const tag = tageDE[i];
                const datumObj = new Date(startDate);
                datumObj.setDate(startDate.getDate() + i);
                
                // Formatiere das Datum als DD.MM.YYYY
                const tagNum = datumObj.getDate().toString().padStart(2, '0');
                const monat = (datumObj.getMonth() + 1).toString().padStart(2, '0');
                const jahr = datumObj.getFullYear();
                
                wochentage[tag] = `${tagNum}.${monat}.${jahr}`;
              }
            }
          } catch (error) {
            console.error("Fehler beim Parsen des Datums:", error);
          }
        }
      }
      
      console.log("Berechnete Wochentage mit Datum:", wochentage);
      
      // Index der Zeile mit Wochentagsbezeichnungen finden (Montag, Dienstag, etc.)
      let headerRowIndex = -1;
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i] || [];
        if (row.some(cell => cell === 'Montag' || cell === 'Dienstag' || 
                    cell === 'Mittwoch' || cell === 'Donnerstag' || 
                    cell === 'Freitag')) {
          headerRowIndex = i;
          break;
        }
      }
      
      if (headerRowIndex === -1) {
        console.error("Wochentagsüberschriften nicht gefunden");
        setUploadStatus('error');
        return;
      }
      
      // Die Spaltenindizes für die Namen und die Bemerkungsspalte mit der Personalnummer bestimmen
      const nameColumnIndex = 0; // Annahme: Name in erster Spalte
      let bemerkungColumnIndex = -1;
      
      // Suche die Bemerkungsspalte im Header
      const headerRow = jsonData[headerRowIndex];
      for (let i = 0; i < headerRow.length; i++) {
        const cell = headerRow[i];
        if (cell === 'Bemerkung') {
          bemerkungColumnIndex = i;
          break;
        }
      }
      
      // Wenn die Bemerkungsspalte nicht gefunden wurde, nehmen wir an, dass sie bei Index 19 ist
      // (basierend auf der Beispieldatei)
      if (bemerkungColumnIndex === -1) {
        bemerkungColumnIndex = 19;
      }
      
      console.log("Bemerkungsspalte gefunden bei Index:", bemerkungColumnIndex);
      
      // Indexe für die einzelnen Tage finden
      const dayIndices = {
        montag: -1,
        dienstag: -1,
        mittwoch: -1,
        donnerstag: -1,
        freitag: -1,
        samstag: -1,
        sonntag: -1
      };
      
      for (let i = 0; i < headerRow.length; i++) {
        const cell = headerRow[i];
        if (cell === 'Montag') dayIndices.montag = i;
        else if (cell === 'Dienstag') dayIndices.dienstag = i;
        else if (cell === 'Mittwoch') dayIndices.mittwoch = i;
        else if (cell === 'Donnerstag') dayIndices.donnerstag = i;
        else if (cell === 'Freitag') dayIndices.freitag = i;
        else if (cell === 'Sa') dayIndices.samstag = i;
        else if (cell === 'So') dayIndices.sonntag = i;
      }
      
      // Wochenplan-Daten verarbeiten
      const weeklyPlans = {};
      
      // Start bei der Zeile nach der Überschrift
      for (let rowIndex = headerRowIndex + 1; rowIndex < jsonData.length; rowIndex++) {
        const weeklyPlanRow = jsonData[rowIndex] || [];
        
        // Name des Mitarbeiters
        const employeeName = weeklyPlanRow[nameColumnIndex];
        
        // Prüfen ob dies eine Zeile mit einem Mitarbeiter ist (Name, Persnr. etc.)
        if (!employeeName || typeof employeeName !== 'string' || employeeName.trim() === '') {
          continue;
        }
        
        // Personalnummer aus der Bemerkungsspalte extrahieren
        let employeeId = null;
        const bemerkungCell = weeklyPlanRow[bemerkungColumnIndex];
        
        if (bemerkungCell && !isNaN(Number(bemerkungCell))) {
          // Wenn die Zelle eine reine Zahl ist, ist das wahrscheinlich die Personalnummer
          employeeId = bemerkungCell;
        } else if (bemerkungCell && typeof bemerkungCell === 'string') {
          // Wenn die Zelle einen Text enthält, versuchen wir die Personalnummer zu extrahieren
          // Typischerweise im Format "10500 Persnr."
          const match = bemerkungCell.match(/(\d+)/);
          if (match && match[1]) {
            employeeId = match[1];
          }
        }
        
        if (!employeeId) {
          console.log(`Keine Personalnummer für ${employeeName} gefunden, überspringe`);
          continue;
        }
        
        console.log(`Verarbeite Wochenplan für Mitarbeiter: ${employeeName}, ID: ${employeeId}`);
        
        // Pausenzeiten aus der Namenszeile extrahieren
        const pausenzeiten = {
          Montag: excelTimeToString(weeklyPlanRow[dayIndices.montag] || ''),
          Dienstag: excelTimeToString(weeklyPlanRow[dayIndices.dienstag] || ''),
          Mittwoch: excelTimeToString(weeklyPlanRow[dayIndices.mittwoch] || ''),
          Donnerstag: excelTimeToString(weeklyPlanRow[dayIndices.donnerstag] || ''),
          Freitag: excelTimeToString(weeklyPlanRow[dayIndices.freitag] || ''),
          Samstag: dayIndices.samstag >= 0 ? excelTimeToString(weeklyPlanRow[dayIndices.samstag] || '') : '',
          Sonntag: dayIndices.sonntag >= 0 ? excelTimeToString(weeklyPlanRow[dayIndices.sonntag] || '') : ''
        };
        
        // Die vier Zeilen des Mitarbeiters verarbeiten
        // Zeile 1: Mitarbeiter + Name (bereits verarbeitet)
        // Zeile 2: "Arbeitszeit *" - Enthält die Arbeitszeiten pro Tag
        // Zeile 3: "Uhrzeit von - bis" - Enthält die Start- und Endzeiten
        // Zeile 4: "abweichender Standort" - Enthält standortinformationen
        // Zeile 5: "Bewertung" - Diese ignorieren wir
        
        const arbeitszeitRowIndex = rowIndex + 1;
        const uhrzeitRowIndex = rowIndex + 2;
        const standortRowIndex = rowIndex + 3;
        
        if (arbeitszeitRowIndex >= jsonData.length || uhrzeitRowIndex >= jsonData.length || standortRowIndex >= jsonData.length) {
          console.log(`Unvollständige Daten für ${employeeName}, überspringe`);
          continue;
        }
        
        const arbeitszeitRow = jsonData[arbeitszeitRowIndex] || [];
        const uhrzeitRow = jsonData[uhrzeitRowIndex] || [];
        const standortRow = jsonData[standortRowIndex] || [];
        
        // Überprüfe, ob die Zeilen den erwarteten Bezeichnungen entsprechen
        if (!arbeitszeitRow[0] || !arbeitszeitRow[0].includes('Arbeitszeit') ||
            !uhrzeitRow[0] || !uhrzeitRow[0].includes('Uhrzeit von - bis') ||
            !standortRow[0] || !standortRow[0].includes('abweichender Standort')) {
          console.log(`Unerwartetes Zeilenlayout für ${employeeName}, überspringe`);
          continue;
        }
        
        // Mitarbeitereintrag erstellen
        if (!weeklyPlans[employeeId]) {
          weeklyPlans[employeeId] = {
            name: employeeName,
            kw: kw,
            zeitraum: zeitraum,
            tage: {}
          };
        }
        
        // Daten für jeden Tag extrahieren
        const tage = ['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag', 'sonntag'];
        const tageDE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
        
        for (let i = 0; i < tage.length; i++) {
          const tag = tage[i];
          const tagDE = tageDE[i];
          const colIndex = dayIndices[tag];
          
          if (colIndex === -1) continue; // Tag nicht gefunden
          
          // Arbeitszeit für den Tag (z.B. "08:30")
          let arbeitszeit = arbeitszeitRow[colIndex] || '';
          arbeitszeit = excelTimeToString(arbeitszeit);
          
          // Uhrzeiten von-bis
          let uhrzeitVon = uhrzeitRow[colIndex] || '';
          let uhrzeitBis = uhrzeitRow[colIndex + 1] || '';
          
          uhrzeitVon = excelTimeToString(uhrzeitVon);
          uhrzeitBis = excelTimeToString(uhrzeitBis);
          
          // Standort
          let standort = standortRow[colIndex] || '';

          // Pausenzeit aus der Namenszeile für diesen Tag
          let pause = pausenzeiten[tagDE];
          
          // Datum für diesen Tag aus den berechneten Wochentagen
          let datum = wochentage[tagDE];
          
          weeklyPlans[employeeId].tage[tagDE] = {
            arbeitszeit: arbeitszeit,
            uhrzeitVon: uhrzeitVon,
            uhrzeitBis: uhrzeitBis,
            standort: standort,
            pause: pause,
            datum: datum
          };
        }
        
        // Springe zum nächsten Mitarbeiter (4 Zeilen pro Mitarbeiter)
        rowIndex += 4;
      }
      
      console.log("Finale Wochenplan-Daten:", weeklyPlans);
      
      if (Object.keys(weeklyPlans).length === 0) {
        console.error('Keine gültigen Wochenpläne gefunden');
        setUploadStatus('error');
        return;
      }
      
      const token = localStorage.getItem('accessToken');
      const url = 'https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/addWeeklyPlanVersion2';
      
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          weeklyPlans: weeklyPlans,
          token: token
        })
      })
        .then(response => response.json())
        .then(result => {
          if (result.success) {
            setUploadStatus('success');
          } else {
            setUploadStatus('error');
          }
          console.log('Add Weekly Plans result:', result);
        })
        .catch(error => {
          console.error('Error adding weekly plans:', error);
          setUploadStatus('error');
        });
    };
  
    reader.readAsArrayBuffer(file);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const { getRootProps: getHolidayRootProps, getInputProps: getHolidayInputProps } = useDropzone({ onDrop: onHolidayDrop });
  const { getRootProps: getWeeklyPlanRootProps, getInputProps: getWeeklyPlanInputProps } = useDropzone({ onDrop: onWeeklyPlanDrop });

  useEffect(() => {
    if (fileData) {
      addAnnualPlans();
    }
  }, [fileData]);

  const addAnnualPlans = async () => {
    console.log("Processing annual plans for multiple employees");
    
    const dateRow = fileData[3];
    // Convert any Excel date serial numbers to JavaScript Date objects
    for (let i = 0; i < dateRow.length; i++) {
      const cell = dateRow[i];
      if (typeof cell === 'number') {
      // Excel date serial numbers can be converted to JS dates
      // Excel's epoch starts on 1899-12-30
      const excelEpoch = new Date(1899, 11, 30);
      const millisecondsPerDay = 24 * 60 * 60 * 1000;
      dateRow[i] = new Date(excelEpoch.getTime() + cell * millisecondsPerDay);
      console.log(`Converted Excel date at index ${i}: ${cell} -> ${dateRow[i]}`);
      } else if (cell instanceof Date) {
      console.log(`Date already in correct format at index ${i}: ${cell}`);
      }
    }
    
    // Alle Mitarbeiterzeilen durchlaufen, beginnend mit Zeile 4
    const annualPlans = {};
    
    for (let rowIndex = 4; rowIndex < fileData.length; rowIndex++) {
      const annualPlanRow = fileData[rowIndex];
      
      // Überprüfen, ob die Zeile gültige Mitarbeiterdaten enthält
      if (!annualPlanRow || 
          !annualPlanRow[0] || 
          !annualPlanRow[2] || 
          typeof annualPlanRow[2] !== 'number') {
        console.log(`Skipping row ${rowIndex}: Invalid data format`, annualPlanRow);
        continue;
      }

      const employeeId = annualPlanRow[2];
      const employeeName = annualPlanRow[0];
      
      if (!annualPlans[employeeId]) {
        annualPlans[employeeId] = [];
      }
      
      console.log(`Processing annual plan for employee: ${employeeName}, ID: ${employeeId}`);

      for (let j = 8; j < annualPlanRow.length; j++) { // Changed <= to < to prevent out of bounds
        const hours = annualPlanRow[j];
        // Check if hours is a number or a string that can be converted to a number
        if (typeof hours === 'number' || !isNaN(Number(hours))) {
          const dateValue = dateRow[j];
          
          if (dateValue && dateValue instanceof Date) {
            const day = dateValue.getDate();
            const month = dateValue.getMonth() + 1;
            
            console.log(`Found working hours for ${employeeName} (${day}.${month}): ${hours} hours`);
            
            // Convert hours format from comma to decimal point if needed
            const normalizedHours = typeof hours === 'string' ? 
              hours.replace(',', '.') : hours;
              
            annualPlans[employeeId].push({
              date: `${day}.${month}`,
              hours: normalizedHours
            });
          }
        }
      }
      
      console.log(`Found ${annualPlans[employeeId].length} working day entries for employee ${employeeId} (${employeeName})`);
    }
    
    console.log("Final annual plans data:", annualPlans);
    
    // Implementierung des Batch-Uploads
    const url = 'https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/addAnnualPlanVersion2';
    const employeeIds = Object.keys(annualPlans);
    const totalEmployees = employeeIds.length;
    
    if (totalEmployees === 0) {
      console.error('No valid annual plans found to upload');
      setUploadStatus('error');
      return;
    }

    // Aufteilung in Batches von max. 20 Mitarbeitern
    const batchSize = 20;
    const batches = [];
    let currentBatch = [];
    
    for (let i = 0; i < employeeIds.length; i++) {
      currentBatch.push(employeeIds[i]);
      
      // Wenn der Batch voll ist oder wir am Ende angekommen sind, füge den Batch hinzu
      if (currentBatch.length === batchSize || i === employeeIds.length - 1) {
        batches.push([...currentBatch]);
        currentBatch = [];
      }
    }
    
    console.log(`Splitting upload into ${batches.length} batches of max ${batchSize} employees each`);
    
    // Status-Tracking für jeden Batch
    const batchStatus = batches.map(() => ({ success: false, retries: 0 }));
    const maxRetries = 3;
    
    try {
      // Sequentiell alle Batches verarbeiten
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const batchPlans = {};
        
        // Extrahiere nur die Daten für die Mitarbeiter in diesem Batch
        batch.forEach(employeeId => {
          batchPlans[employeeId] = annualPlans[employeeId];
        });
        
        // Validiere die Batch-Daten vor dem Senden
        if (Object.keys(batchPlans).length === 0) {
          console.error(`Batch ${batchIndex + 1}/${batches.length} contains no valid data, skipping`);
          continue;
        }
        
        let batchSuccess = false;
        let attempts = 0;
        
        // Wiederholungsmechanismus für fehlgeschlagene Batch-Uploads
        while (!batchSuccess && attempts < maxRetries) {
          attempts++;
          console.log(`Processing batch ${batchIndex + 1}/${batches.length}, attempt ${attempts}/${maxRetries}`);
          
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                annualPlans: batchPlans,
                token: token
              }),
              // Timeout erhöhen, um Netzwerkprobleme zu vermeiden
              timeout: 30000
            });
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success) {
              console.log(`Batch ${batchIndex + 1}/${batches.length} uploaded successfully`);
              batchSuccess = true;
              batchStatus[batchIndex].success = true;
            } else {
              console.error(`Batch ${batchIndex + 1}/${batches.length} failed: ${result.message || 'Unknown error'}`);
              // Warte ein wenig vor dem nächsten Versuch
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } catch (error) {
            console.error(`Error processing batch ${batchIndex + 1}/${batches.length}:`, error);
            // Warte ein wenig vor dem nächsten Versuch
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
          batchStatus[batchIndex].retries = attempts;
        }
      }
      
      // Überprüfe den Gesamtstatus
      const allSuccess = batchStatus.every(status => status.success);
      const anySuccess = batchStatus.some(status => status.success);
      
      if (allSuccess) {
        console.log('All batches uploaded successfully');
        setUploadStatus('success');
      } else if (anySuccess) {
        console.log('Some batches uploaded successfully, but not all');
        setUploadStatus('partialSuccess');
      } else {
        console.error('All batches failed to upload');
        setUploadStatus('error');
      }
      
      console.log('Batch upload status:', batchStatus);
    } catch (error) {
      console.error('Error in batch processing:', error);
      setUploadStatus('error');
    }
  };

  const handleCloseDialog = () => {
    setUploadStatus(null);
  };

  const downloadAnnualPlanTemplate = () => {
    saveAs(jahresrahmendienstplanTemplate, 'Jahresrahmendienstplan.xlsx');
  };

  const downloadHolidayPlanTemplate = () => {
    saveAs(urlaubsplanTemplate, 'Urlaubsplan.xlsx');
  };

  const downloadWeeklyPlanTemplate = () => {
    saveAs(wochenarbeitsplanTemplate, 'Wochenarbeitsplan.xlsx');
  };

  return (
    <div className="py-[32px] px-[90px]">
      <h1 className="text-2xl font-bold">Uploads</h1>
      <p className="mb-[46px] text-gray-700">
        Hier kannst du Jahres-, Urlaubs- und Wochenpläne hochladen.
      </p>

      <div className="flex mb-[46px]">
        <div className="bg-white shadow-md rounded-xl w-full p-[16px] flex">
          <DocumentIcon className="h-5 w-5 mr-2 text-[#A7B3C4] self-start mt-1" />
          <div>
            <h2 className="text-xl font-bold text-[#A7B3C4]">Jahresplan</h2>
            <p className="text-sm text-[#A7B3C4] mb-4">Letzte Aktualisierung: 23.05.2023</p>
            <div className='border-t' />
            <div className="flex mt-[16px]">
              <button {...getRootProps()} className="text-primary py-2 px-4 rounded-lg flex items-center mr-2 hover:text-primaryDark transition-colors">
                <input {...getInputProps()} />
                <PlusIcon className="h-5 w-5 mr-2" />
                Neuen Jahresplan anlegen
              </button>
              <button onClick={downloadAnnualPlanTemplate} className="text-primary py-2 px-4 rounded-lg flex items-center hover:text-primaryDark transition-colors">
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Jahresplan-Vorlage herunterladen
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex mb-[46px]">
        <div className="bg-white shadow-md rounded-xl w-full p-[16px] flex">
          <DocumentIcon className="h-5 w-5 mr-2 text-[#A7B3C4] self-start mt-1" />
          <div>
            <h2 className="text-xl font-bold text-[#A7B3C4]">Urlaubsplan</h2>
            <p className="text-sm text-[#A7B3C4] mb-4">Letzte Aktualisierung: 23.05.2023</p>
            <div className='border-t' />
            <div className="flex mt-[16px]">
              <button {...getHolidayRootProps()} className="text-primary py-2 px-4 rounded-lg flex items-center mr-2 hover:text-primaryDark transition-colors">
                <input {...getHolidayInputProps()} />
                <PlusIcon className="h-5 w-5 mr-2" />
                Neuen Urlaubsplan anlegen
              </button>
              <button onClick={downloadHolidayPlanTemplate} className="text-primary py-2 px-4 rounded-lg flex items-center hover:text-primaryDark transition-colors">
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Urlaubsplan-Vorlage herunterladen
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex mb-[46px]">
        <div className="bg-white shadow-md rounded-xl w-full p-[16px] flex">
          <DocumentIcon className="h-5 w-5 mr-2 text-[#A7B3C4] self-start mt-1" />
          <div>
            <h2 className="text-xl font-bold text-[#A7B3C4]">Wochenplan</h2>
            <p className="text-sm text-[#A7B3C4] mb-4">Letzte Aktualisierung: 23.05.2023</p>
            <div className='border-t' />
            <div className="flex mt-[16px]">
              <button {...getWeeklyPlanRootProps()} className="text-primary py-2 px-4 rounded-lg flex items-center mr-2 hover:text-primaryDark transition-colors">
                <input {...getWeeklyPlanInputProps()} />
                <PlusIcon className="h-5 w-5 mr-2" />
                Neuen Wochenplan anlegen
              </button>
              <button onClick={downloadWeeklyPlanTemplate} className="text-primary py-2 px-4 rounded-lg flex items-center hover:text-primaryDark transition-colors">
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Wochenplan-Vorlage herunterladen
              </button>
            </div>
          </div>
        </div>
      </div>

      {uploadStatus && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-10 rounded-lg shadow-lg w-3/4 max-w-3xl h-auto text-center">
            {uploadStatus === 'success' ? (
              <>
                <img src="/login-success.png" alt="Success" className="mx-auto mb-4 w-20 h-20" />
                <h2 className="text-2xl font-bold text-black mb-4">Upload erfolgreich!</h2>
              </>
            ) : uploadStatus === 'partialSuccess' ? (
              <>
                <img src="/login-success.png" alt="Partial Success" className="mx-auto mb-4 w-20 h-20" />
                <h2 className="text-2xl font-bold text-black mb-4">Upload teilweise erfolgreich</h2>
                <p className="text-gray-700 mb-4">Einige Mitarbeiterdaten wurden erfolgreich hochgeladen, andere konnten nicht verarbeitet werden. Sie können es später erneut versuchen.</p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-black mb-4">Upload fehlgeschlagen</h2>
                <p className="text-gray-700 mb-4">Bitte überprüfe das Format deiner Datei und versuche es erneut.</p>
              </>
            )}
            <button onClick={handleCloseDialog} className="bg-primary text-textLight py-2 px-6 rounded-md hover:bg-primaryDark transition-colors">Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Uploads;